import { Badge, BadgeProps } from "@mantine/core"
import { ReactNode } from "react"

type StatusLike = {
  code?: string
  color?: string
  sentName?: string
  name?: string
}

type Props = {
  /**
   * Either a resolved status object (with .color and .sentName/.name) from
   * the Redux general reference lists, or a raw color string. When both
   * color and label are passed as separate props, those override the status.
   */
  status?: StatusLike | null
  /** Raw color override — takes precedence over status.color */
  color?: string
  /** Raw label override — takes precedence over status.sentName/name */
  label?: ReactNode
  /** Placeholder rendered when nothing resolves (defaults to "-") */
  placeholder?: string
  /** Passed through to Mantine Badge */
  size?: BadgeProps["size"]
  /** Passed through to Mantine Badge */
  radius?: BadgeProps["radius"]
  /** Passed through to Mantine Badge */
  variant?: BadgeProps["variant"]
}

const FALLBACK_COLOR = "var(--mantine-color-gray-6)"

/**
 * Status pill driven by either a Redux status object (has .code / .color /
 * .sentName) or raw color + label props.
 *
 * Two visual flavors via `variant`:
 * - "light"  (default): 12% color-mix tinted background + full-color text,
 *   matching detail-header / detail-sidebar treatments.
 * - "outline": 1px solid color border + full-color text, no background,
 *   matching the table-cell status pills used in list-columns / kanban.
 */
export function StatusBadge({
  status,
  color,
  label,
  placeholder = "-",
  size = "md",
  radius = "md",
  variant = "light",
}: Props) {
  const resolvedColor = color ?? status?.color ?? FALLBACK_COLOR
  const resolvedLabel = label ?? status?.sentName ?? status?.name ?? placeholder

  const isOutline = variant === "outline"
  const style = isOutline
    ? { border: `1px solid ${resolvedColor}`, color: resolvedColor, background: "transparent" }
    : { background: `color-mix(in srgb, ${resolvedColor} 12%, transparent)`, color: resolvedColor }

  return (
    <Badge
      size={size}
      radius={radius}
      variant={variant}
      style={style}
      styles={{ label: { overflow: "visible" } }}
    >
      {resolvedLabel}
    </Badge>
  )
}
